import type { CSSProperties, FormEvent } from "react"
import type { FormType } from "../formType"
import { formTypeFields } from "../formType"
import type { CommunicationConsent } from "../types"
import { communicationConsentKey } from "../parseCommunicationConsents"

interface FormScreenProps {
  styles: Record<string, CSSProperties>
  formType: FormType
  formTitle: string
  formSubtitle?: string
  firstName: string
  firstNamePlaceholder: string
  email: string
  emailPlaceholder: string
  phone: string
  phonePlaceholder: string
  state: string
  stateLabel: string
  states: string[]
  consentToProcessText?: string
  consentToProcess: boolean
  communicationConsents: CommunicationConsent[]
  communicationValues: Record<string, boolean>
  submitButtonText: string
  recaptchaText?: string
  recaptchaBranding?: boolean
  error?: string
  submitting: boolean
  onFirstNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onPhoneChange: (value: string) => void
  onStateChange: (value: string) => void
  onConsentToProcessChange: (value: boolean) => void
  onCommunicationChange: (key: string, value: boolean) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export default function FormScreen({
  styles,
  formType,
  formTitle,
  formSubtitle,
  firstName,
  firstNamePlaceholder,
  email,
  emailPlaceholder,
  phone,
  phonePlaceholder,
  state,
  stateLabel,
  states,
  consentToProcessText,
  consentToProcess,
  communicationConsents,
  communicationValues,
  submitButtonText,
  recaptchaText,
  recaptchaBranding,
  error,
  submitting,
  onFirstNameChange,
  onEmailChange,
  onPhoneChange,
  onStateChange,
  onConsentToProcessChange,
  onCommunicationChange,
  onSubmit,
}: FormScreenProps) {
  const fields = formTypeFields(formType)

  return (
    <>
      <h1 id="form-popup-form-title" style={styles.formTitle}>
        {formTitle}
      </h1>
      {formSubtitle?.trim() ? (
        <p id="form-popup-form-subtitle" style={styles.formSubtitle}>
          {formSubtitle}
        </p>
      ) : null}

      <form id="form-popup-form" style={styles.form} onSubmit={onSubmit} noValidate>
        <div style={styles.inputGroup}>
          {fields.firstName ? (
            <>
              <label htmlFor="form-popup-first-name" className="sr-only">
                First name
              </label>
              <input
                id="form-popup-first-name"
                className="form-popup-input"
                name="firstname"
                type="text"
                autoComplete="given-name"
                required
                value={firstName}
                placeholder={firstNamePlaceholder}
                onChange={(event) => onFirstNameChange(event.target.value)}
                style={styles.textInput}
                aria-required="true"
              />
            </>
          ) : null}

          <label htmlFor="form-popup-email" className="sr-only">
            Email
          </label>
          <input
            id="form-popup-email"
            className="form-popup-input"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            placeholder={emailPlaceholder}
            onChange={(event) => onEmailChange(event.target.value)}
            style={styles.textInput}
            aria-required="true"
          />

          {fields.phone ? (
            <>
              <label htmlFor="form-popup-phone" className="sr-only">
                Phone
              </label>
              <input
                id="form-popup-phone"
                className="form-popup-input"
                name="phone"
                type="tel"
                autoComplete="tel"
                required
                value={phone}
                placeholder={phonePlaceholder}
                onChange={(event) => onPhoneChange(event.target.value)}
                style={styles.textInput}
                aria-required="true"
              />
            </>
          ) : null}
        </div>

        {fields.state ? (
          <>
            <p id="form-popup-state-label" style={styles.stateLabel}>
              {stateLabel}
            </p>
            <div
              role="radiogroup"
              aria-labelledby="form-popup-state-label"
              aria-required="true"
              style={styles.stateGrid}
            >
              {states.map((option) => {
                const id = `form-popup-state-${option.replace(/\s+/g, "-").toLowerCase()}`
                return (
                  <label key={option} htmlFor={id} style={styles.radioRow}>
                    <input
                      id={id}
                      type="radio"
                      name="form-popup-state"
                      value={option}
                      checked={state === option}
                      onChange={() => onStateChange(option)}
                      style={styles.radio}
                    />
                    {option}
                  </label>
                )
              })}
            </div>
          </>
        ) : null}

        {consentToProcessText || communicationConsents.length > 0 ? (
          <div style={styles.consentList}>
            {consentToProcessText ? (
              <label htmlFor="form-popup-consent-process" style={styles.consentRow}>
                <input
                  id="form-popup-consent-process"
                  type="checkbox"
                  checked={consentToProcess}
                  required
                  aria-required="true"
                  onChange={(event) => onConsentToProcessChange(event.target.checked)}
                  style={styles.consentCheckbox}
                />
                <span>
                  {consentToProcessText}
                  <span style={styles.consentRequired} aria-hidden="true">
                    *
                  </span>
                </span>
              </label>
            ) : null}
            {communicationConsents.map((consent) => {
              const key = communicationConsentKey(consent)
              const id = `form-popup-consent-comm-${key}`
              return (
                <label key={key} htmlFor={id} style={styles.consentRow}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={Boolean(communicationValues[key])}
                    required={consent.required}
                    aria-required={consent.required ? true : undefined}
                    onChange={(event) =>
                      onCommunicationChange(key, event.target.checked)
                    }
                    style={styles.consentCheckbox}
                  />
                  <span>
                    {consent.text}
                    {consent.required ? (
                      <span style={styles.consentRequired} aria-hidden="true">
                        *
                      </span>
                    ) : null}
                  </span>
                </label>
              )
            })}
          </div>
        ) : null}

        <button
          id="form-popup-submit"
          type="submit"
          style={styles.submitButton}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : submitButtonText}
        </button>

        {error ? (
          <p id="form-popup-error" role="alert" style={styles.error}>
            {error}
          </p>
        ) : null}
      </form>

      {recaptchaBranding ? (
        <p id="form-popup-recaptcha" style={styles.recaptcha}>
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            style={styles.recaptchaLink}
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            style={styles.recaptchaLink}
          >
            Terms of Service
          </a>{" "}
          apply.
        </p>
      ) : recaptchaText?.trim() ? (
        <p id="form-popup-recaptcha" style={styles.recaptcha}>
          {recaptchaText}
        </p>
      ) : null}
    </>
  )
}
